import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Trip, Client, ClientTrip } from "@shared/schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { 
  Plus, 
  Search, 
  Calendar, 
  MapPin, 
  Users,
  PencilIcon,
  Trash2Icon,
  EyeIcon,
  ArrowLeft,
  Plane,
  UserIcon
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { format, addDays } from "date-fns";
import { useLocation } from "wouter";
import TripFormDialog from "@/components/TripFormDialog";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const toCountryList = (countries: Trip["countries"]): string[] => {
  if (Array.isArray(countries)) {
    return countries
      .filter((country): country is string => typeof country === "string")
      .map((country) => country.trim())
      .filter(Boolean);
  }

  if (typeof countries === "string") {
    const trimmed = countries.trim();
    return trimmed ? [trimmed] : [];
  }

  return [];
};

const getTripDestination = (trip: Trip): string | null => {
  const record = trip as Record<string, unknown>;

  if (typeof record.destination === "string" && record.destination.trim()) {
    return record.destination.trim();
  }

  const countries = toCountryList(trip.countries);
  if (countries.length > 0) {
    return countries[0];
  }

  return null;
};

const getTripDescription = (trip: Trip): string | null => {
  const record = trip as Record<string, unknown>;

  if (typeof record.description === "string" && record.description.trim()) {
    return record.description.trim();
  }

  if (typeof trip.clientInterests === "string" && trip.clientInterests.trim()) {
    return trip.clientInterests.trim();
  }

  return null;
};

// Trip List Component
const TripsList: React.FC<{
  onTripSelect: (trip: Trip) => void;
  selectedTripId?: number;
}> = ({ onTripSelect, selectedTripId }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("all");
  const [tripToDelete, setTripToDelete] = useState<Trip | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showCreateTripDialog, setShowCreateTripDialog] = useState(false);
  const [tripToEdit, setTripToEdit] = useState<Trip | null>(null);
  const { toast } = useToast();

  const { data: tripsResponse, isLoading, error } = useQuery<Trip[]>({
    queryKey: ['/api/trips', { page: currentPage, pageSize, search: searchTerm, filter: filterBy }],
    queryFn: async () => {
      const response = await fetch(`/api/trips`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch trips');
      }
      
      const data = await response.json();
      return data;
    }
  });

  // Fetch contacts for each trip
  const { data: contactsData } = useQuery<ClientTrip[]>({
    queryKey: ['/api/contact-trips'],
    queryFn: async () => {
      const response = await fetch('/api/contact-trips', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.ok ? response.json() : [];
    }
  });

  const { data: allContacts } = useQuery<Client[]>({
    queryKey: ['/api/contacts'],
    queryFn: async () => {
      const response = await fetch('/api/contacts', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      return response.ok ? response.json() : [];
    }
  });

  // Handle different API response formats
  const trips = Array.isArray(tripsResponse) 
    ? tripsResponse 
    : tripsResponse || [];

  // Filter trips based on search term
  const filteredTrips = trips.filter(trip => {
    if (!searchTerm) return true;
    const destination = getTripDestination(trip);
    return (
      (trip.name && trip.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (destination && destination.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  });

  // Sort trips by updated date (most recent first)
  const sortedTrips = filteredTrips.sort((a, b) => {
    const dateA = new Date(a.updatedAt || a.createdAt || a.startDate);
    const dateB = new Date(b.updatedAt || b.createdAt || b.startDate);
    return dateB.getTime() - dateA.getTime();
  });

  const getTripContacts = (tripId: number) => {
    if (!contactsData || !allContacts) return [];
    
    const tripContactIds = contactsData
      .filter(ct => ct.tripId === tripId)
      .map(ct => ct.clientId);
    
    return allContacts.filter(contact => tripContactIds.includes(contact.id));
  };

  const handleDeleteTrip = async () => {
    if (!tripToDelete) return;
    
    setIsDeleting(true);
    try {
      const response = await fetch(`/api/trips/${tripToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete trip');
      }

      toast({
        title: "Trip deleted",
        description: "The trip has been successfully deleted.",
      });

      // Refetch trips
      // This would trigger a refetch if using React Query
      window.location.reload();
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete trip. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
      setTripToDelete(null);
    }
  };

  const handleTripCreated = () => {
    setShowCreateTripDialog(false);
    // Refetch trips
    window.location.reload();
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-red-600 mb-2">Failed to load trips</p>
          <Button variant="outline" onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Trips</h2>
            <p className="text-gray-600 dark:text-gray-400">Manage your travel itineraries</p>
          </div>
          <Button 
            className="bg-orange-600 hover:bg-orange-700"
            onClick={() => setShowCreateTripDialog(true)}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Trip
          </Button>
        </div>

        {/* Search and Filters */}
        <div className="flex gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search trips..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterBy} onValueChange={setFilterBy}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filter by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Trips</SelectItem>
              <SelectItem value="upcoming">Upcoming</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Trip List */}
        <Card>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-4 space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center space-x-4">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="space-y-2 flex-1">
                      <Skeleton className="h-4 w-40" />
                      <Skeleton className="h-3 w-60" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Trip Name</TableHead>
                    <TableHead>Destination</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Countries</TableHead>
                    <TableHead>Travelers</TableHead>
                    <TableHead>Updated</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTrips.map((trip) => {
                    const tripContacts = getTripContacts(trip.id);
                    const tripDescription = getTripDescription(trip);
                    const tripDestination = getTripDestination(trip);
                    const countries = toCountryList(trip.countries);
                    return (
                      <TableRow
                        key={trip.id}
                        className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 ${
                          selectedTripId === trip.id ? 'bg-orange-50 dark:bg-orange-900/30 border-l-4 border-orange-500' : ''
                        }`}
                        onClick={() => onTripSelect(trip)}
                      >
                        <TableCell>
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                              <Plane className="h-4 w-4 text-orange-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-gray-100">
                                {trip.name || 'Untitled Trip'}
                              </p>
                              {tripDescription && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                                  {tripDescription}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-gray-600 dark:text-gray-300">
                            <MapPin className="h-3 w-3 mr-2" />
                            {tripDestination ?? 'No destination'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-gray-600 dark:text-gray-300">
                            <Calendar className="h-3 w-3 mr-2" />
                            {trip.startDate && trip.endDate ? (
                              <>
                                {format(new Date(trip.startDate), 'MMM d')} - {format(new Date(trip.endDate), 'MMM d, yyyy')}
                              </>
                            ) : (
                              'No dates set'
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          {countries.length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {countries.slice(0, 2).map((country) => (
                                <Badge key={country} variant="outline" className="text-xs">
                                  {country}
                                </Badge>
                              ))}
                              {countries.length > 2 && (
                                <Badge variant="outline" className="text-xs">
                                  +{countries.length - 2}
                                </Badge>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-500 dark:text-gray-400 text-sm">No countries</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {tripContacts.length > 0 ? (
                            <div className="flex -space-x-2">
                              {tripContacts.slice(0, 3).map((contact) => (
                                <Avatar key={contact.id} className="h-8 w-8 border-2 border-background">
                                  <AvatarFallback className="bg-cyan-100 text-cyan-800 text-xs">
                                    {contact.firstName?.charAt(0)}{contact.lastName?.charAt(0)}
                                  </AvatarFallback>
                                </Avatar>
                              ))}
                              {tripContacts.length > 3 && (
                                <div className="h-8 w-8 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs border-2 border-background dark:text-gray-300">
                                  +{tripContacts.length - 3}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center text-gray-500 dark:text-gray-400">
                              <UserIcon className="h-4 w-4 mr-1" />
                              <span className="text-sm">No travelers</span>
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600 dark:text-gray-300">
                            {(() => {
                              const dateValue = trip.updatedAt || trip.createdAt;
                              if (!dateValue) return 'No date';
                              const date = new Date(dateValue);
                              if (isNaN(date.getTime())) return 'Invalid date';
                              return format(date, 'MMM d, yyyy');
                            })()}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setTripToEdit(trip);
                              }}
                            >
                              <PencilIcon className="h-4 w-4" />
                              <span className="sr-only">Edit trip</span>
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="sm" 
                              className="h-8 w-8 p-0 text-destructive hover:text-destructive" 
                              onClick={(e) => {
                                e.stopPropagation();
                                setTripToDelete(trip);
                              }}
                            >
                              <Trash2Icon className="h-4 w-4" />
                              <span className="sr-only">Delete trip</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {sortedTrips.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <Plane className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">No trips found</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {searchTerm ? 'Try adjusting your search terms.' : 'Get started by creating your first trip.'}
            </p>
            <Button 
              className="bg-orange-600 hover:bg-orange-700"
              onClick={() => setShowCreateTripDialog(true)}
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Trip
            </Button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!tripToDelete} onOpenChange={() => !isDeleting && setTripToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete this trip?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{tripToDelete?.name}" and all its components.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTrip}
              disabled={isDeleting}
              className="bg-red-500 hover:bg-red-600 focus:bg-red-600"
            >
              {isDeleting ? "Deleting..." : "Delete Trip"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create Trip Dialog */}
      <TripFormDialog 
        open={showCreateTripDialog}
        onOpenChange={setShowCreateTripDialog}
        onSuccess={handleTripCreated}
      />

      {/* Edit Trip Dialog */}
      {tripToEdit && (
        <TripFormDialog 
          open={!!tripToEdit}
          onOpenChange={(open) => !open && setTripToEdit(null)}
          trip={tripToEdit}
        />
      )}
    </>
  );
};

// Trip Detail Component
const TripDetail: React.FC<{
  trip: Trip;
  onBack: () => void;
}> = ({ trip, onBack }) => {
  const [, navigate] = useLocation();

  const tripDestination = getTripDestination(trip);
  const tripDescription = getTripDescription(trip);
  const countries = toCountryList(trip.countries);

  const handleViewFullTrip = () => {
    navigate(`/trips/${trip.id}`);
  };

  const handleViewFullTripNewTab = () => {
    window.open(`/trips/${trip.id}`, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Trips
        </Button>
      </div>

      {/* Trip Header */}
      <Card>
        <CardHeader>
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center">
              <Plane className="h-8 w-8 text-orange-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-2xl">{trip.name || 'Untitled Trip'}</CardTitle>
                             <div className="space-y-2">
                 {tripDestination && (
                   <div className="flex items-center gap-2">
                     <MapPin className="h-4 w-4" />
                     <span className="text-gray-600">{tripDestination}</span>
                   </div>
                 )}
                 {trip.startDate && trip.endDate && (
                   <div className="flex items-center gap-2">
                     <Calendar className="h-4 w-4" />
                     <span className="text-gray-600">
                       {format(new Date(trip.startDate), 'MMM d')} - {format(new Date(trip.endDate), 'MMM d, yyyy')}
                     </span>
                   </div>
                 )}
                 {tripDescription && (
                   <p className="text-gray-600 mt-2">{tripDescription}</p>
                 )}
               </div>
            </div>
                         <div className="flex gap-2">
               <Button onClick={handleViewFullTrip}>
                 <EyeIcon className="h-4 w-4 mr-2" />
                 View Full Trip
               </Button>
               <Button variant="outline" onClick={handleViewFullTripNewTab}>
                 <EyeIcon className="h-4 w-4 mr-2" />
                 Open in New Tab
               </Button>
             </div>
          </div>
        </CardHeader>
      </Card>

      {/* Trip Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <Calendar className="h-8 w-8 mx-auto mb-2 text-blue-600" />
              <h3 className="font-medium">Duration</h3>
              <p className="text-sm text-gray-600">
                {trip.startDate && trip.endDate ? (
                  `${Math.ceil((new Date(trip.endDate).getTime() - new Date(trip.startDate).getTime()) / (1000 * 60 * 60 * 24))} days`
                ) : (
                  'Not set'
                )}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <MapPin className="h-8 w-8 mx-auto mb-2 text-green-600" />
              <h3 className="font-medium">Countries</h3>
              <p className="text-sm text-gray-600">
                {countries.length} {countries.length === 1 ? 'country' : 'countries'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="text-center">
              <Users className="h-8 w-8 mx-auto mb-2 text-purple-600" />
              <h3 className="font-medium">Status</h3>
              <p className="text-sm text-gray-600">Planning</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Countries */}
      {countries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Countries</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {countries.map((country) => (
                <Badge key={country} variant="outline">
                  {country}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
                     <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
             <Button variant="outline" onClick={handleViewFullTrip}>
               <EyeIcon className="h-4 w-4 mr-2" />
               View Itinerary
             </Button>
             <Button variant="outline" onClick={handleViewFullTripNewTab}>
               <EyeIcon className="h-4 w-4 mr-2" />
               Open in New Tab
             </Button>
             <Button variant="outline">
               <Calendar className="h-4 w-4 mr-2" />
               Edit Dates
             </Button>
             <Button variant="outline">
               <MapPin className="h-4 w-4 mr-2" />
               Add Destinations
             </Button>
           </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Canvas Trips Component
const CanvasTrips: React.FC = () => {
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [view, setView] = useState<'list' | 'detail'>('list');

  const handleTripSelect = (trip: Trip) => {
    setSelectedTrip(trip);
    setView('detail');
  };

  const handleBackToList = () => {
    setSelectedTrip(null);
    setView('list');
  };

  return (
    <div className="h-full bg-white dark:bg-gray-900">
      <div className="h-full overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {view === 'list' ? (
            <TripsList 
              onTripSelect={handleTripSelect}
              selectedTripId={selectedTrip?.id}
            />
          ) : (
            selectedTrip && (
              <TripDetail 
                trip={selectedTrip}
                onBack={handleBackToList}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default CanvasTrips;
